import type { FeatureFlagKey } from '@sb/feathers-types';
import type { MotionChildTCPOption } from '@sb/motion-planning';
import { isSymbolicTooltip } from '@sb/motion-planning';
import type { StepKind } from '@sb/routine-runner';

/**
 * Shared source of truth for Weld permitted child step kinds. Mirrors the
 * UI behavior but is exported from the validation library so both UI and
 * server can consume the same rule.
 */
export function getPermittedChildStepKindsShared(
  featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {},
): readonly StepKind[] {
  const permittedSteps: StepKind[] = ['Waypoint'];

  if (!featureFlags.disableArcStepInWeld) {
    permittedSteps.push('Arc');
  }

  return permittedSteps;
}

/**
 * The same for a rotary weld, which takes no `Arc` and no feature flag: the
 * positioner turns the part while the robot holds still, so there is no travel
 * for an arc to shape. The count — exactly one — is the validation rule's.
 */
export function getRotaryWeldPermittedChildStepKindsShared(): readonly StepKind[] {
  return ['Waypoint'];
}

/**
 * The step kinds that pin a motion child's tooltip to the torch the step
 * selects, so the child's own tooltip fields are dead data.
 *
 * Kept apart from `WAYPOINT_PARENT_STEP_KINDS` because they answer different
 * questions — permitting a waypoint and overriding its tooltip are independent,
 * and a step can do either without the other.
 */
export const TORCH_PINNING_STEP_KINDS: readonly StepKind[] = [
  'Weld',
  'RotaryWeld',
];

export function pinsChildTooltip(kind: string | null | undefined): boolean {
  return (
    kind != null &&
    (TORCH_PINNING_STEP_KINDS as readonly string[]).includes(kind)
  );
}

/**
 * The step kinds that strike an arc on a weld machine, naming it through
 * `selectedMachineID`.
 *
 * A third list rather than a reuse of the two above, for the same reason they
 * are separate from each other: a step could pin its child's tooltip without
 * arcing, or arc without holding waypoints at all. Read by the play flow, which
 * has to find every machine a run could arc on before it can offer dry run or
 * check that the welder is live.
 */
export const ARC_STEP_KINDS: readonly StepKind[] = ['Weld', 'RotaryWeld'];

export function strikesArc(kind: string | null | undefined): boolean {
  return kind != null && (ARC_STEP_KINDS as readonly string[]).includes(kind);
}

/**
 * Whether a motion child's `tcpOption` names a frame outright, rather than one
 * of the symbolic members that name a frame only once the routine runs.
 *
 * A symbolic selection deliberately does not count. Every new waypoint and arc
 * created outside a Weld step is seeded `'parent'` — only children created
 * directly inside one are seeded bare — so counting a symbolic member would
 * raise a blocking error on any ordinary motion child dragged or pasted into a
 * Weld step, one the user could not clear: the weld tooltip row is read-only and
 * offers no way to name nothing at all. Inside a Weld step the selection is dead
 * data anyway, the torch being the highest-precedence frame the runner resolves.
 */
export function hasExplicitTcpOption(args: {
  tcpOption: MotionChildTCPOption;
}): boolean {
  return !isSymbolicTooltip(args.tcpOption);
}

/**
 * What a Waypoint or Arc inside a Weld step is told when it names a tooltip:
 * the Weld step pins its children to the selected torch, so the child has made
 * a choice the runner will ignore.
 */
export const WELD_CHILD_TOOLTIP_ACTION_REQUIRED = {
  kind: 'invalidConfiguration',
  message:
    'The tooltip is set by the Weld step, so this step cannot select its own.',
  fieldId: 'tcpOption',
} as const;
